import { WarningOutlined } from '@ant-design/icons';
import { Button, Col, Form, Row } from 'antd';
import moment from 'moment';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { formatDateToLocalTimeZone } from '../../../utils/dateFormatter';
import {
  ErrorMessage,
  StyledCard,
  StyledDatePicker,
  StyledFormItem,
  Title,
} from './style';

interface Props {
  status?: string;
  apiKey?: string;
  inactiveSince: any;
  revokedBy?: string;
  onReactivate: Function;
  onCancel: Function;
}

const ReactivateAPIKeyInstanceForm = ({
  status,
  apiKey,
  inactiveSince,
  revokedBy,
  onReactivate,
  onCancel,
}: Props) => {
  const initialValues = {
    activeFrom: moment(),
    expiresAt: undefined,
  };

  const [currentValues, setCurrentValues] = useState<any>(initialValues);
  const [isValidDateRange, setIsValidDateRange] = useState(true);

  const disabledStartDates = (current: any) =>
    current < moment().subtract(1, 'days');
  const disabledEndDates = (current: any) => {
    let disabledDates;

    if (current) {
      if (currentValues.activeFrom) {
        disabledDates =
          current <= moment(currentValues.activeFrom).endOf('day');
      } else {
        disabledDates = current <= moment().endOf('day');
      }
    }

    return disabledDates;
  };

  const validateRange = (dateRange: any) => {
    const { activeFrom, expiresAt } = dateRange;
    return moment(expiresAt).isAfter(activeFrom, 'day');
  };

  return (
    <Row justify="center">
      <Title>
        <FormattedMessage id="ApiKeys.modal.subTitle.Reactivate" />
      </Title>
      {status === 'Expired' ? (
        <p>This API key is expired</p>
      ) : (
        <p>
          <FormattedMessage id="ApiKeys.modal.title.deactivated" />
        </p>
      )}

      <Col span={24}>
        <StyledCard>
          <Row>
            <Col span={9}>
              <b>
                <FormattedMessage id="ApiKeys.modal.label" />
              </b>
            </Col>
            <Col span={15}>{apiKey}</Col>
          </Row>
          <Row>
            <Col span={9}>
              <b>
                <FormattedMessage id="ApiKeys.modal.label.inactiveSince" />
              </b>
            </Col>
            <Col span={15}>{formatDateToLocalTimeZone(inactiveSince)}</Col>
          </Row>

          {revokedBy && (
            <Row>
              <Col span={9}>
                <b>
                  <FormattedMessage id="ApiKeys.modal.label.deactivatedby" />
                </b>
              </Col>
              <Col span={15}>{revokedBy}</Col>
            </Row>
          )}
        </StyledCard>
      </Col>

      <Col span={24}>
        <Form
          layout="vertical"
          initialValues={initialValues}
          onFinish={value => onReactivate(value)}
          onValuesChange={(_, allValues) => {
            setCurrentValues(allValues);
            setIsValidDateRange(validateRange(allValues));
          }}
        >
          <Row gutter={[10, 0]} style={{ marginBottom: '20px' }}>
            <Col span={12}>
              <StyledFormItem
                label={<FormattedMessage id="ApiKeys.modal.label.activefrom" />}
                name="activeFrom"
              >
                <StyledDatePicker
                  data-testid="api-key-instance_activeFrom"
                  showTime
                  disabledDate={disabledStartDates}
                />
              </StyledFormItem>
            </Col>
            <Col span={12}>
              <StyledFormItem
                label={
                  <FormattedMessage id="ApiKeys.modal.label.activeuntil" />
                }
                name="expiresAt"
              >
                <StyledDatePicker
                  data-testid="api-key-instance_activeUntil"
                  showTime
                  disabledDate={disabledEndDates}
                />
              </StyledFormItem>
            </Col>
            {!isValidDateRange && (
              <Col span={12}>
                <ErrorMessage>
                  <span className="icon-wraper">
                    <WarningOutlined data-testid="WarningOutlinedIcon" />
                  </span>
                  <FormattedMessage id="ApiKeys.data.error" />
                </ErrorMessage>
              </Col>
            )}
          </Row>

          <Row justify="center" gutter={[15, 0]}>
            <Col>
              <Button
                type="default"
                shape="round"
                size="large"
                onClick={() => onCancel()}
              >
                <FormattedMessage id="Global.cancel" />
              </Button>
            </Col>
            <Col>
              <Button
                disabled={
                  !currentValues.activeFrom ||
                  !currentValues.expiresAt ||
                  !isValidDateRange
                }
                type="primary"
                shape="round"
                size="large"
                htmlType="submit"
              >
                <FormattedMessage id="Global.confirm" />
              </Button>
            </Col>
          </Row>
        </Form>
      </Col>
    </Row>
  );
};

ReactivateAPIKeyInstanceForm.defaultProps = {
  apiKey: '',
  status: 'Expired',
  revokedBy: '',
};

export default ReactivateAPIKeyInstanceForm;
